/**
 * English strings — the source of truth. `fa.ts` is typed against this object,
 * so adding a key here is a compile error until Farsi is supplied too.
 *
 * `{n}`, `{x}` etc. are interpolated by `t(key, params)`.
 */
export const en = {
  /* ------------------------------------------------------------ app shell */
  'app.tagline': 'Ala · Personal Hub',
  'nav.today': 'Today',
  'nav.calendar': 'Calendar',
  'nav.study': 'Study',
  'nav.journey': 'Journey',
  'nav.library': 'Library',
  'nav.more': 'More',

  /* -------------------------------------------------------------- common */
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.add': 'Add',
  'common.done': 'Done',
  'common.none': 'none',
  'common.of': 'of',
  'common.arrow': '→',

  /* ---------------------------------------------------------------- time */
  'time.morning': 'Good morning',
  'time.afternoon': 'Good afternoon',
  'time.evening': 'Good evening',
  'time.today': 'today',
  'time.tomorrow': 'tomorrow',
  'time.yesterday': 'yesterday',
  'time.inDays': 'in {n} days',
  'time.daysAgo': '{n} days ago',
  'time.overdue': 'overdue',
  'time.dueToday': 'due today',
  'time.days': '{n} d',
  'time.weeks': '{n} wks',
  'time.hours': '{n}h',
  'time.minutes': '{n}m',
  'time.hoursMinutes': '{h}h {m}m',
  'time.hoursShort': '{n} h',
  'time.minutesShort': '{n} m',

  /* --------------------------------------------------------------- today */
  'today.comma': ',',
  'today.customise': 'Customise',
  'today.openTasks': '{n} open tasks',
  'today.openTask': '{n} open task',
  'today.nothingOpen': 'nothing open',
  'today.streak': '{n}-day streak',
  'today.toInstitution': '{time} to {name}',

  'tile.countdown': 'deadline',
  'tile.nextUp': 'next up',
  'tile.tasks': 'tasks',
  'tile.studyWeek': 'study week',
  'tile.audition': 'audition',
  'tile.quickNote': 'quick note',
  'tile.weather': 'weather',
  'tile.player': 'player',

  'tile.moveEarlier': 'Move earlier',
  'tile.moveLater': 'Move later',
  'tile.resize': 'Resize',
  'tile.hide': 'Hide {name}',

  'tile.nextUpTitle': 'Next up',
  'tile.nothingScheduled': 'Nothing scheduled. Enjoy it',
  'tile.tasksTitle': 'Tasks',
  'tile.all': 'all',
  'tile.allClear': 'All clear.',
  'tile.thisWeek': 'This week',
  'tile.ofTarget': 'of {x} target',
  'tile.auditionTitle': 'Audition',
  'tile.piecesInProgress': 'pieces in progress',
  'tile.pieceInProgress': 'piece in progress',
  'tile.quickNoteTitle': 'Quick note',
  'tile.quickNotePlaceholder': 'one line before it flies away…',
  'tile.pinIt': 'Pin it',
  'tile.notes': 'notes',
  'tile.nowPlaying': 'Now playing',
  'tile.nothingYet': 'Nothing yet',
  'tile.pickSomething': 'pick something from the library',
  'tile.library': 'library',
  'tile.journeyTitle': 'Journey',
  'tile.addFirstApplication': 'Add your first application',
  'tile.requirementsDone': '{done}/{total} requirements done',

  'weather.checking': 'Checking the sky…',
  'weather.unavailable': 'Weather unavailable',
  'weather.clear': 'clear sky',
  'weather.partlyCloudy': 'partly cloudy',
  'weather.overcast': 'overcast',
  'weather.rain': 'rain',
  'weather.snow': 'snow',
  'weather.showers': 'showers',
  'weather.storms': 'storms',

  'player.play': 'Play',
  'player.pause': 'Pause',

  /* ------------------------------------------------------------ calendar */
  'calendar.title': 'Calendar',
  'calendar.event': 'Event',
  'calendar.month': 'Month',
  'calendar.agenda': 'Agenda',
  'calendar.everythingAhead': 'Everything ahead',
  'calendar.prevMonth': 'Previous month',
  'calendar.nextMonth': 'Next month',
  'calendar.dayLabel': '{date}, {n} events',
  'calendar.nothingOn': 'Nothing on',
  'calendar.nothingOnBody': 'Tap “Event” to put something in.',
  'calendar.clearHorizon': 'Clear horizon',
  'calendar.clearHorizonBody': 'Nothing scheduled from today onward.',
  'calendar.newEvent': 'New event',
  'calendar.editEvent': 'Edit event',
  'calendar.deleteEvent': 'Delete event',
  'calendar.markDone': 'Mark done',
  'calendar.markNotDone': 'Mark not done',
  'calendar.fieldTitle': 'Title',
  'calendar.fieldDate': 'Date',
  'calendar.fieldStart': 'Start',
  'calendar.fieldEnd': 'End',
  'calendar.fieldKind': 'Kind',
  'calendar.fieldNotes': 'Notes',
  'calendar.titlePlaceholder': 'Composition tutorial',
  'calendar.notesPlaceholder': 'Anything to remember…',

  'kind.class': 'Class',
  'kind.study': 'Study',
  'kind.deadline': 'Deadline',
  'kind.audition': 'Audition',
  'kind.personal': 'Personal',

  /* --------------------------------------------------------------- study */
  'study.title': 'Study',
  'study.oneSession': 'One session at a time',
  'study.streakKeep': '{n}-day streak — keep it',
  'study.tabFocus': 'Focus',
  'study.tabTasks': 'Tasks',
  'study.tabSubjects': 'Subjects',
  'study.phaseFocus': 'Focus',
  'study.phaseBreak': 'Break',
  'study.start': 'Start',
  'study.pause': 'Pause',
  'study.resume': 'Resume',
  'study.reset': 'Reset',
  'study.logAndStop': 'Log & stop',
  'study.thisWeek': 'This week',
  'study.weekSummary': '{done} of {target}',
  'study.addTaskPlaceholder': 'Add a task…',
  'study.newTaskLabel': 'New task',
  'study.addWithDetails': 'Add with details',
  'study.openCount': 'Open · {n}',
  'study.nothingOpen': 'Nothing open',
  'study.nothingOpenBody': 'Everything on your list is done.',
  'study.doneSection': 'Done',
  'study.newTask': 'New task',
  'study.fieldTask': 'Task',
  'study.taskPlaceholder': 'Finish the arrangement…',
  'study.fieldSubject': 'Subject',
  'study.fieldDue': 'Due',
  'study.important': 'important',
  'study.addTask': 'Add task',
  'study.addSubject': 'Add subject',
  'study.subjectName': 'Subject name',
  'study.subjectPlaceholder': 'Composition',
  'study.weeklyTarget': 'Weekly target',
  'study.targetPerWeek': 'target {x} per week',
  'study.targetHours': '{name} weekly target in hours',
  'study.setColour': 'Set {name} colour to {hue}',
  'study.deleteNamed': 'Delete {name}',

  /* ------------------------------------------------------------- journey */
  'journey.title': 'Journey',
  'journey.subtitle': 'Every application, and what each one still needs',
  'journey.whatsNext': "What's next",
  'journey.noApplications': 'No applications yet',
  'journey.noApplicationsBody':
    "Add the first university you're aiming for and track its requirements here.",
  'journey.deadline': 'Deadline {date}',
  'journey.addRequirement': 'Add a requirement…',
  'journey.newRequirement': 'New requirement',
  'journey.removeRequirement': 'Remove {name}',
  'journey.newApplication': 'New application',
  'journey.editApplication': 'Edit application',
  'journey.deleteApplication': 'Delete application',
  'journey.fieldInstitution': 'Institution',
  'journey.fieldCourse': 'Course',
  'journey.fieldDeadline': 'Deadline',
  'journey.fieldStatus': 'Status',
  'journey.fieldNotes': 'Notes',
  'journey.institutionPlaceholder': 'Adelaide University — Elder Conservatorium',
  'journey.coursePlaceholder': 'Bachelor of Music (Creative Practice)',
  'journey.notesPlaceholder': 'What matters about this one…',

  'status.researching': 'Researching',
  'status.preparing': 'Preparing',
  'status.submitted': 'Submitted',
  'status.interview': 'Interview',
  'status.offer': 'Offer',
  'status.declined': 'Declined',

  /* ------------------------------------------------------------- library */
  'library.title': 'Library',
  'library.subtitle': 'Repertoire, pages and loose thoughts',
  'library.tabMusic': 'Music',
  'library.tabWiki': 'Wiki',
  'library.tabNotes': 'Notes',
  'library.allTracks': 'all tracks',
  'library.auditionOnly': 'audition only',
  'library.upload': 'Upload',
  'library.uploadLabel': 'Upload an audio file',
  'library.uploadHint':
    "Uploaded audio plays for this session only — the file itself stays on your device and isn't copied into the app's storage.",
  'library.nothingHere': 'Nothing here',
  'library.nothingHereBody': 'Upload a take to start building the audition.',
  'library.auditionBadge': 'audition',
  'library.myUpload': 'my upload',
  'library.playNamed': 'Play {name}',
  'library.pauseNamed': 'Pause {name}',
  'library.removeNamed': 'Remove {name}',
  'library.noPages': 'No pages yet',
  'library.noPagesBody': 'Wiki pages are markdown — good for research and notes.',
  'library.newPage': 'New page',
  'library.newPageShort': '+ new',
  'library.untitledPage': 'Untitled page',
  'library.livePreview': 'Live preview',
  'library.editedRel': 'edited {when}',
  'library.markdownSource': 'Markdown source',
  'library.quickNote': 'Quick note',
  'library.quickNotePlaceholder': 'write in any language…',
  'library.pinNote': 'Pin note',
  'library.nothingPinned': 'Nothing pinned',
  'library.nothingPinnedBody': 'Pin a quick note and it lands here.',
  'library.deleteNote': 'Delete note',

  /* ------------------------------------------------------------ settings */
  'settings.title': 'Settings',
  'settings.subtitle': 'Everything is stored on this device',
  'settings.blockedTitle': "Changes aren't being saved",
  'settings.blockedBody':
    'Browser storage is full or disabled (private browsing does this). The app still works, but anything you add will be lost when you close the tab.',
  'settings.you': 'You',
  'settings.name': 'Name',
  'settings.city': 'City (used for weather)',
  'settings.appearance': 'Appearance',
  'settings.language': 'Language',
  'settings.languageHint': 'Switches the interface. Anything you have written stays as you wrote it.',
  'settings.theme': 'Theme',
  'settings.themeHint': "System follows your device's light/dark setting.",
  'settings.themeLight': 'light',
  'settings.themeDark': 'dark',
  'settings.themeSystem': 'system',
  'settings.accent': 'Accent',
  'settings.accentHint': 'Used for highlights, focus rings and progress.',
  'settings.plan': 'The plan',
  'settings.intake': 'Target intake',
  'settings.intakeHint':
    'Drives the seeded timeline. Changing it only re-dates the plan while the app is still untouched — once you have edited things, your own dates are kept.',
  'settings.focusTimer': 'Focus timer',
  'settings.focusBlock': 'Focus block',
  'settings.break': 'Break',
  'settings.timerLength': '{label} length in minutes',
  'settings.yourData': 'Your data',
  'settings.dataHint':
    "Nothing leaves this device — there's no account and no server. That also means clearing your browser data clears the app, so export a backup now and then. Using {kb} KB.",
  'settings.export': 'Export backup',
  'settings.import': 'Import backup',
  'settings.chooseBackup': 'Choose a backup file',
  'settings.resetTitle': 'Reset everything',
  'settings.resetHint':
    "Deletes all your events, tasks, applications and notes, and restores the starting content. This can't be undone.",
  'settings.resetConfirm': 'Yes, erase it',
  'settings.reset': 'Reset',

  'intake.aug2027': 'August 2027 — second intake',
  'intake.feb2028': 'February 2028',

  'toast.backupDownloaded': 'Backup downloaded',
  'toast.backupRestored': 'Backup restored',
  'toast.importFailed': 'Import failed — {error}',
  'toast.everythingReset': 'Everything reset',
  'toast.replanned': 'Plan re-dated for {intake}',
  'toast.keptYourDates': 'Interface switched. Your own content was kept as written.',
}

export type TKey = keyof typeof en
